import React from 'react';

import { useLevelFiltersDevice } from '@/data-explorer-v2/hooks/use-level-filters-device';
import { useCurrentAsset } from '@/hooks/use-current-asset';

import { GetLevels } from '../../api/queries/get-levels';
import type { BatteryLevel } from '../../entities/battery-level';
import type { AllIdFilters } from '../../entities/filters';
import { getParentIdForLevel } from '../../utils/parent-id-logic';

import type { Domain } from './domain-toggle';
import { LevelFilter } from './level-filter';

type LevelFiltersProps = {
  domain: Domain;
  data: GetLevels.Output | undefined;
  isLoading: boolean;
  filters: AllIdFilters;
  onChange: (filters: Partial<AllIdFilters>) => void;
};

/**
 * Helper to determine if a level should be enabled based on parent selection count
 */
function isLevelEnabled(
  levelIndex: number,
  levelsOrder: BatteryLevel[],
  filters: AllIdFilters,
): boolean {
  // First level (array/pcs) is always enabled
  if (levelIndex === 0) {
    return true;
  }

  // Check if parent level has exactly 1 selection
  const parentLevel = levelsOrder[levelIndex - 1];
  const parentValue = filters[`${parentLevel}Id`];

  if (!parentValue) {
    return false;
  }

  // Enable only if parent has exactly 1 selection
  const selectionCount = Array.isArray(parentValue) ? parentValue.length : 1;
  return selectionCount === 1;
}

/**
 * Helper to clear all deeper levels when a parent becomes multi-selected
 */
function clearDeeperLevels(
  changedLevel: BatteryLevel,
  levelsOrder: BatteryLevel[],
): Partial<AllIdFilters> {
  const changedIndex = levelsOrder.indexOf(changedLevel);
  const clearingUpdates: Partial<AllIdFilters> = {};

  // Clear all levels deeper than the changed level
  for (let i = changedIndex + 1; i < levelsOrder.length; i++) {
    const levelToClose = levelsOrder[i];
    const filterKey = `${levelToClose}Id` as const;
    clearingUpdates[filterKey] = undefined;
  }

  return clearingUpdates;
}

export const LevelFilters: React.FC<LevelFiltersProps> = ({
  domain,
  data,
  isLoading,
  filters,
  onChange,
}) => {
  const asset = useCurrentAsset();
  // Pull out the mapping for the chosen category, and cast it so TS knows the keys are BatteryLevel
  const levelsMap = (data?.[domain] ?? {}) as Record<BatteryLevel, string>;

  // Derive an ordered array of the accure levels
  const levelsOrder = Object.keys(levelsMap) as BatteryLevel[];

  // Enhanced onChange handler with cascade clearing logic
  const handleChange = (partial: Partial<AllIdFilters>) => {
    // Find which level was changed
    const changedLevel = Object.keys(partial).find((key) =>
      key.endsWith('Id'),
    ) as keyof AllIdFilters;

    if (changedLevel) {
      const level = changedLevel.replace('Id', '') as BatteryLevel;
      const newValue = partial[changedLevel];
      const currentValue = filters[changedLevel];

      // Check if this level changed from single (1) to multi (2+) selection
      const wasCurrentlySingle =
        currentValue &&
        (!Array.isArray(currentValue) || currentValue.length === 1);
      const isBecomingMulti =
        newValue && Array.isArray(newValue) && newValue.length >= 2;

      if (wasCurrentlySingle && isBecomingMulti) {
        // Clear all deeper levels when transitioning from single to multi
        const clearingUpdates = clearDeeperLevels(level, levelsOrder);
        onChange({ ...partial, ...clearingUpdates });
        return;
      }
    }

    // Normal change without cascade clearing
    onChange(partial);
  };

  // Precompute each filter's config: level, customerLabel, parentId, and whether it's enabled
  const filterConfigs = levelsOrder.map((level, index) => ({
    level,
    customerLabel: levelsMap[level],
    parentId: getParentIdForLevel(
      level,
      levelsOrder,
      filters,
      asset.accureId ?? '',
    ),
    disabled: !isLevelEnabled(index, levelsOrder, filters),
    parentLevel: index > 0 ? levelsOrder[index - 1] : undefined,
    parentLabel: index > 0 ? levelsMap[levelsOrder[index - 1]] : undefined,
  }));

  useLevelFiltersDevice({
    id: filters.deviceId,
    enabledLevels: filterConfigs
      .filter(({ disabled }) => !disabled)
      .map(({ level }) => level),
    onChange,
    filters,
  });

  if (isLoading) {
    return <div className="p-4 text-gray-500">Loading levels data...</div>;
  }
  if (!data || Object.keys(data).length === 0) {
    return <div className="p-4 text-gray-500">No levels data available</div>;
  }

  return (
    <div className="flex flex-row gap-4">
      <div className="flex gap-2">
        {filterConfigs.map(
          ({
            level,
            customerLabel,
            parentId,
            disabled,
            parentLevel,
            parentLabel,
          }) => (
            <LevelFilter
              key={level}
              level={level}
              customerLabel={customerLabel}
              parentId={parentId}
              filters={filters}
              onChange={handleChange}
              disabled={disabled}
              parentLevel={parentLevel}
              parentLabel={parentLabel}
            />
          ),
        )}
      </div>
    </div>
  );
};
